import { Tooltip, Text, Badge, Card, ScrollArea, Table } from '@mantine/core'
import { useMemo } from 'react'
import { GroupData } from '../service/types/interfaces'
import { useArcTranslation } from 'hooks'
import { useNavigate } from 'react-router-dom'
import { EmptyStateBox } from 'components'
import { IconArticleOff } from '@tabler/icons-react'

const processData = (rawData: GroupData[], t: any): Record<string, any>[] => {
  const processedData: Record<string, any>[] = []

  rawData.forEach(group => {
    const row: Record<string, any> = {
      group: group.group,
      group_id: group.groupId,
    }

    group.data.forEach(item => {
      const key = Object.keys(item)[0]
      const dataItem = item[key]

      if (dataItem !== null) {
        row[key] = `${dataItem.percentage}% | ${dataItem.average || 0}`
      }
    })

    processedData.push(row)
  })

  return processedData
}

const getGroupIdByName = (data: GroupData[], groupName: string): string | undefined => {
  const group = data.find(g => g.group === groupName)
  return group ? group.groupId : undefined
}

const generateColumns = (
  data: GroupData[],
  t: any,
  handleGroupClick: (groupName: string) => void,
): Array<{ accessorKey: string; header: JSX.Element; Cell?: any }> => {
  if (!data || data.length === 0) return []

  const sampleData = data[0].data
  const keys = sampleData.map(item => Object.keys(item)[0])
  const headers = sampleData.map(item => item[Object.keys(item)[0]]?.lv1Item)

  return [
    {
      accessorKey: 'group',
      header: t('reportTableHeaders.group').slice(0, 15),
      Cell: ({ row, cell }: { row: any; cell: any }) => (
        <span style={{ display: 'block', width: 'fit-content' }}>
          <Tooltip label={t('show_group')} withArrow position='top'>
            <Text onClick={() => handleGroupClick(cell.getValue())} style={{ cursor: 'pointer', fontWeight: 'bold' }}>
              {cell.getValue()}
            </Text>
          </Tooltip>
        </span>
      ),
    },
    ...keys.map((key, index) => ({
      accessorKey: key,
      header: (
        <Tooltip label={headers[index]} withArrow position='top'>
          <Text style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {headers[index]?.slice(0, 15)}
          </Text>
        </Tooltip>
      ),
      Cell: ({ cell }: { cell: { getValue: () => any } }) => {
        const value = cell.getValue() || ''
        const [percentage = '0', average = '0'] = value.split('|').map((v: string) => v.trim())
        const averageValue = parseFloat(average) || 0

        let color = 'green'
        if (averageValue <= 2) color = 'red'
        else if (averageValue === 3) color = 'blue'

        if (value.length === 0) {
          return <span>-</span>
        }

        return (
          <Tooltip label={t('expectation_tooltip')} withArrow position='top'>
            <Text style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <Badge color={color} style={{ marginRight: 4 }}>
                {averageValue}
              </Badge>
              {percentage !== null && <span>{` | ${percentage}`}</span>}
            </Text>
          </Tooltip>
        )
      },
    })),
  ]
}

interface ReportTableProps {
  data: GroupData[]
}

export default function ReportTable({ data }: ReportTableProps) {
  const { t } = useArcTranslation()
  const navigate = useNavigate()

  const tableData = useMemo(() => processData(data, t), [data])
  const handleGroupClick = (groupName: string) => {
    const groupId = getGroupIdByName(data, groupName)
    if (groupId) {
      navigate(`/reports/group/analitical/${groupId}?groupName=${groupName}`)
    }
  }
  const columns = useMemo(() => generateColumns(data, t, handleGroupClick), [data, t])

  if (tableData?.length === 0) {
    return <EmptyStateBox withBorder icon={<IconArticleOff />} text={t('no_items_found')} />
  }

  return (
    <Card shadow='sm' padding='lg'>
      <ScrollArea>
        <Table>
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th key={index}>{column.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((column, colIndex) => (
                  <td key={colIndex}>
                    {column.Cell
                      ? column.Cell({ row, cell: { getValue: () => row[column.accessorKey] } })
                      : row[column.accessorKey]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </ScrollArea>
    </Card>
  )
}
